"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {
  NeoButton,
  NeoDatePicker,
  NeoSelect,
  NeoTextBox
} from "@/components/forms";
import {UploadFormField} from "@/components/pages/expensereports/uploadform";
import {PlusIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {FleetService} from "@/service/fleet.service";
import {yupResolver} from "@hookform/resolvers/yup";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectItem
} from "@nextui-org/react";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import Select from "react-select";
import * as yup from "yup";

export type FormFuel = {
  vehicleId: string;
  id?: string;
  date?: Date | null;
  fuelType: string;
  qty: number;
  totalAmount: number;
  fuelRate: number;
  fuelStationId?: string | null;
  odometerReading?: number;
  description?: string | null;
};

type FuelFormProps = {
  data: any;
  onSubmit: (formValues: any) => void | boolean | Promise<boolean | void>;
  onClose: any;
};

type FormViewMode = "single" | "bulkPreview";
type CalculationMethod = "totalAmount" | "qty" | "fuelRate";

const CALCULATION_OPTIONS: Array<{
  value: CalculationMethod;
  label: string;
  description: string;
}> = [
  {
    value: "fuelRate",
    label: "Rate per liter",
    description:
      "Enter total cost and quantity. Rate per liter is filled automatically."
  },
  {
    value: "qty",
    label: "Quantity",
    description:
      "Enter total cost and rate per liter. Quantity is filled automatically."
  },
  {
    value: "totalAmount",
    label: "Total cost",
    description:
      "Enter quantity and rate per liter. Total cost is filled automatically."
  }
];

const FORM_VIEW_OPTIONS = [
  {
    value: "single" as const,
    label: "Single Entry",
    description: "Manual form"
  },
  {
    value: "bulkPreview" as const,
    label: "Bulk Upload",
    description: "Excel upload"
  }
] as const;

const FIELD_LABEL_CLASSNAME =
  "inline-flex items-center gap-1 text-[15px] font-semibold tracking-[-0.01em] text-slate-900";

const getFuelFieldClassNames = (isAutoCalculated = false) => ({
  label:
    "text-[15px] font-semibold tracking-[-0.01em] text-slate-900 group-data-[filled-within=true]:text-slate-900",
  inputWrapper: `h-14 min-h-14 rounded-[18px] border-[1.5px] border-slate-200 bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition data-[hover=true]:border-slate-300 group-data-[focus=true]:border-emerald-400 ${
    isAutoCalculated
      ? "border-emerald-200 bg-emerald-50/80 data-[hover=true]:border-emerald-300 group-data-[focus=true]:border-emerald-400"
      : ""
  }`,
  input: `text-[15px] text-slate-900 placeholder:text-slate-400 ${
    isAutoCalculated ? "font-semibold text-emerald-950" : ""
  }`,
  helperWrapper: "px-1 pt-2",
  description: "text-xs leading-5 text-slate-500",
  errorMessage: "text-xs font-medium"
});

const getReactSelectStyles = (isInvalid = false) => ({
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "56px",
    borderWidth: "1.5px",
    borderColor: isInvalid
      ? "#f31260"
      : state.isFocused
        ? "#34d399"
        : "#cbd5e1",
    borderRadius: "18px",
    paddingLeft: "6px",
    paddingRight: "6px",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    fontSize: "15px",
    backgroundColor: "white",
    cursor: "pointer",
    "&:hover": {
      borderColor: isInvalid ? "#f31260" : "#94a3b8"
    }
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 8px"
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#94a3b8"
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#0f172a"
  }),
  indicatorSeparator: () => ({display: "none"}),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#64748b"
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: "10px",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 20px 40px rgba(15, 23, 42, 0.12)",
    overflow: "hidden",
    zIndex: 50
  }),
  menuList: (base: any) => ({
    ...base,
    padding: "8px"
  }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "12px",
    padding: "12px 14px",
    backgroundColor: state.isSelected
      ? "#ecfdf5"
      : state.isFocused
        ? "#f8fafc"
        : "white",
    color: "#0f172a",
    cursor: "pointer"
  })
});

export const ExpenseFuelForm: React.FC<FuelFormProps> = ({data, onSubmit}) => {
  const fueltypes = useMemo(
    () => [
      {id: "1", name: "Diesel"},
      {id: "2", name: "Petrol"}
    ],
    []
  );
  const maxDate = useMemo(() => new Date(), []);
  const isCreateMode = !data?.id;
  const {filterContext} = useNeoContext();
  const [loading] = useState(false);
  const [loginUserId, setLoginUserId] = useState("");
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [fuelstations, setFuelStations] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [defaultformdata, setdefaultformdata] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalName, setModalName] = useState("");
  const [modalData] = useState(null);
  const [activeFormView, setActiveFormView] = useState<FormViewMode>("single");
  const [bulkExcelFile, setBulkExcelFile] = useState<File | null>(null);
  const [bulkExcelError, setBulkExcelError] = useState("");
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [calculationMethod, setCalculationMethod] =
    useState<CalculationMethod>("totalAmount");
  const [isCalculationPopoverOpen, setIsCalculationPopoverOpen] =
    useState(false);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const toNumOrUndef = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    if (typeof v === "string") {
      const s = v.trim().replace(/,/g, "");
      if (!s) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  const odoTransform = (_: unknown, o: unknown) => {
    if (o === "" || o == null) return undefined; // optional if empty
    if (typeof o === "number") return Number.isFinite(o) ? o : NaN;

    const s = String(o).trim().replace(/,/g, "");
    // integer only: use /^\d+$/ ; for decimals (2 dp), use /^\d+(\.\d{1,2})?$/
    if (!/^\d+$/.test(s)) return NaN; // â†’ triggers typeError
    return Number(s);
  };

  const schema = yup.object().shape({
    vehicleId: yup.string().required("Please select vehicle"),
    id: yup.string().optional(),
    date: yup
      .date()
      .transform((v, o) => {
        if (o === "" || o == null) return null;
        const d = new Date(o);
        return isNaN(d.getTime()) ? null : d;
      })
      .nullable()
      .typeError("Enter a valid date")
      .max(today, "Date cannot be in the future"),
    fuelType: yup.string().required("Please select fuel type"),
    qty: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Quantity must be a number")
      .moreThan(0, "Quantity must be greater than 0")
      .required("Please enter quantity"),
    totalAmount: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Total cost must be a number")
      .moreThan(0, "Total cost must be greater than 0")
      .max(100000, "Total cost must be less than or equal to 1,00,000")
      .required("Please enter total cost"),
    fuelRate: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Rate must be a number")
      .moreThan(0, "Rate must be greater than 0")
      .max(100000, "Total cost must be less than or equal to 1,00,000")
      .required("Please enter rate per liter"),
    fuelStationId: yup.string().nullable(),
    odometerReading: yup
      .number()
      .transform(odoTransform)
      .typeError("Odometer must be a number")
      .min(0, "Odometer cannot be negative")
      .optional(),
    description: yup.string().nullable().default("")
  });

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    reset,
    formState: {isSubmitting, errors}
  } = useForm<FormFuel>({
    mode: "onChange",
    resolver: yupResolver<FormFuel>(schema),
    shouldUnregister: false
  });

  const handleApiResponse = useCallback(
    (apiResponse: any) => {
      const formValues: FormFuel = {
        vehicleId: apiResponse?.vehicleId ?? "",
        date: apiResponse?.date,
        fuelType: apiResponse?.fuelType ?? "",
        qty: toNumOrUndef(apiResponse?.qty) ?? 0,
        totalAmount: toNumOrUndef(apiResponse?.totalAmount) ?? 0,
        fuelRate: toNumOrUndef(apiResponse?.fuelRate) ?? 0,
        fuelStationId: apiResponse?.fuelStationId ?? "",
        odometerReading: toNumOrUndef(apiResponse?.odometerReading) ?? 0,
        description: apiResponse?.description ?? ""
      };
      reset(formValues);
    },
    [reset]
  );

  useEffect(() => {
    setValue("date", maxDate);
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext?.client?.institutionUserId, maxDate, setValue]);

  const fetchVehicleDatas = useCallback(async () => {
    try {
      const requestData = {
        filters: {
          branch: [loginUserId]
        }
      };
      const response = await ExpenseService.getvehicles(requestData);
      if (
        response?.data?.status === "success" &&
        response?.data?.data?.reportData
      ) {
        setVehicles(response.data.data.reportData);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const fetchFuelStations = useCallback(async () => {
    try {
      const response = await FleetService.getfleetdatas(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        const list = response.data.data?.["fuelStations"] ?? [];
        const updatedList = [
          {id: "", name: "Please select fuel station"},
          ...list
        ];
        setFuelStations(updatedList);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const getfueldata = useCallback(async () => {
    const requestdata = {
      expenseType: 1,
      expenseId: data?.id
    };
    try {
      const response = await ExpenseService.getexpenseview(
        loginUserId,
        requestdata
      );
      if (response?.data?.status === "success" && response?.data?.data) {
        const responsedata = response?.data?.data;
        const responsefueldata = responsedata.fuelExpense;
        setAttachments(
          Array.isArray(responsefueldata?.documents)
            ? responsefueldata.documents
            : []
        );
        const fueldata: any = {
          vehicleId: responsefueldata?.vehicleId,
          date: responsefueldata?.date,
          fuelType: responsefueldata?.fuelTypeId,
          qty: responsefueldata?.quantity,
          totalAmount: responsefueldata?.totalAmount,
          fuelRate: responsefueldata?.ratePerLiter,
          fuelStationId: responsefueldata?.fuelStationId,
          odometerReading: responsefueldata?.odometerReading,
          description: responsefueldata?.description,
          document: responsefueldata.document,
          id: data?.id
        };
        handleApiResponse(fueldata);
        setdefaultformdata(fueldata);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [data?.id, handleApiResponse, loginUserId]);

  useEffect(() => {
    if (!loginUserId) return;
    (async () => {
      try {
        await fetchVehicleDatas();
        await fetchFuelStations();
        if (data?.id) {
          await getfueldata();
        }
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    })();
  }, [
    data?.id,
    fetchFuelStations,
    fetchVehicleDatas,
    getfueldata,
    loginUserId
  ]);

  const onDateChange = (name: keyof FormFuel, event: any) => {
    clearErrors(name);
    setValue(name, event, {shouldDirty: true, shouldValidate: true});
  };

  const onFormSubmit = useCallback(
    async (form: FormFuel) => {
      const payload = {
        ...form,
        id: data?.id ? String(data.id) : undefined,
        attachments
      };
      try {
        await onSubmit(payload);
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    },
    [attachments, data?.id, onSubmit]
  );

  const onCancel = useCallback(() => {
    //console.log(defaultformdata);
    //handleApiResponse(defaultformdata);
    //onClose();
    setAttachments(
      Array.isArray(defaultformdata?.documents) ? defaultformdata.documents : []
    );
    if (data?.id) {
      getfueldata();
    } else {
      handleApiResponse(defaultformdata);
      setValue("date", maxDate);
    }
  }, [
    data?.id,
    defaultformdata,
    getfueldata,
    handleApiResponse,
    maxDate,
    setValue
  ]);

  const openPopup = () => {
    setModalName("fleetform");
    setModalOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    const requestData: Record<string, any> = {
      type: 1,
      name: data.name,
      phone: data.phone,
      address: data.address
    };

    try {
      const response = await FleetService.addfleetdata(
        requestData,
        loginUserId
      );
      if (response?.data?.status === "success") {
        setModalOpen(false);
        await fetchFuelStations();
        toast.success(response?.data?.message);
      } else {
        toast.error(response?.data?.message || "Something went wrong.");
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  };

  const toNum = (v: unknown) => {
    if (v === "" || v === null || v === undefined) return NaN;
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : NaN;
  };
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const isPos = (n: number) => Number.isFinite(n) && n > 0;
  type Field = "totalAmount" | "qty" | "fuelRate";
  const isBulkPreviewActive = isCreateMode && activeFormView === "bulkPreview";
  const isUpdatingRef = useRef(false);
  const setIfChanged = (name: Field, val: number) => {
    const EPS = 0.001;
    const cur = toNum(getValues(name));
    if (!Number.isFinite(val)) return;
    if (!Number.isFinite(cur) || Math.abs(cur - val) > EPS) {
      setValue(name, round2(val), {shouldDirty: true, shouldValidate: true});
    }
  };

  const isAutoCalculatedField = (field: Field) => calculationMethod === field;

  const selectedCalculationOption =
    CALCULATION_OPTIONS.find(option => option.value === calculationMethod) ??
    CALCULATION_OPTIONS[2];

  const getAutoCalculatedDescription = (field: Field) => {
    if (!isAutoCalculatedField(field)) return undefined;

    switch (field) {
      case "qty":
        return "Auto-calculated from total cost and rate per liter.";
      case "fuelRate":
        return "Auto-calculated from total cost and quantity.";
      case "totalAmount":
      default:
        return "Auto-calculated from quantity and rate per liter.";
    }
  };

  const recalculateAutoField = (
    method: CalculationMethod = calculationMethod
  ) => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    try {
      const rate = toNum(getValues("fuelRate"));
      const qty = toNum(getValues("qty"));
      const total = toNum(getValues("totalAmount"));

      const hasRate = isPos(rate);
      const hasQty = isPos(qty);
      const hasTotal = isPos(total);

      switch (method) {
        case "totalAmount":
          if (hasQty && hasRate) {
            setIfChanged("totalAmount", qty * rate);
          }
          break;

        case "qty":
          if (hasTotal && hasRate && rate > 0) {
            setIfChanged("qty", total / rate);
          }
          break;

        case "fuelRate":
          if (hasTotal && hasQty && qty > 0) {
            setIfChanged("fuelRate", total / qty);
          }
          break;
      }
    } finally {
      isUpdatingRef.current = false;
    }
  };

  const handleCalculationMethodChange = (method: CalculationMethod) => {
    setCalculationMethod(method);
    setIsCalculationPopoverOpen(false);
    recalculateAutoField(method);
  };

  const handleFormViewChange = (view: FormViewMode) => {
    if (view === "single") {
      setAttachments(
        Array.isArray(defaultformdata?.documents)
          ? defaultformdata.documents
          : []
      );
      handleApiResponse(defaultformdata);
      if (!data?.id) {
        setValue("date", maxDate);
      }
    }

    setActiveFormView(view);
    setBulkExcelError("");
  };

  const validateBulkExcelFile = (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return ["xlsx", "xls"].includes(extension);
  };

  const handleBulkExcelChange = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    if (!validateBulkExcelFile(file)) {
      setBulkExcelFile(null);
      setBulkExcelError("Please upload an .xlsx or .xls file.");
      toast.error("Please upload an .xlsx or .xls file.");
      return;
    }

    setBulkExcelFile(file);
    setBulkExcelError("");
  };

  const clearBulkExcelFile = () => {
    setBulkExcelFile(null);
    setBulkExcelError("");
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = "";
    }
  };

  const handleBulkUploadSubmit = useCallback(async () => {
    if (!bulkExcelFile) {
      const errorMessage = "Please select an Excel file to continue.";
      setBulkExcelError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setBulkExcelError("");
    setIsBulkUploading(true);
    try {
      await onSubmit({
        bulkUpload: true,
        excelFile: bulkExcelFile
      });
    } catch (error: any) {
      const errorMessage =
        typeof error?.message === "string" && error.message.trim()
          ? error.message
          : "Sorry! We have a technical issue. Please try again later.";

      setBulkExcelError(errorMessage);

      if (!error?.message) {
        toast.error(errorMessage);
      }
    } finally {
      setIsBulkUploading(false);
    }
  }, [bulkExcelFile, onSubmit]);

  const handleTemplateAction = async () => {
    try {
      await ExpenseService.downloadExpenseTemplate({
        type: "fuel",
        vehicles,
        fuelTypes: fueltypes,
        fuelStations: fuelstations
      });
      toast.success("Template downloaded.");
    } catch {
      toast.error("Unable to download template. Please try again later.");
    }
  };

  return (
    <>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-3 sm:p-5">
        <div className="mx-auto w-full max-w-[1120px]">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-600">
                Expense Entry
              </p>
              <h1 className="mt-1 text-[26px] font-semibold tracking-[-0.03em] text-slate-950 sm:text-[30px]">
                Fuel Expense
              </h1>
            </div>

            {isCreateMode && (
              <div className="grid w-full max-w-[420px] grid-cols-2 gap-2 rounded-[20px] border border-slate-200 bg-white p-2 shadow-[0_12px_28px_-26px_rgba(15,23,42,0.2)]">
                {FORM_VIEW_OPTIONS.map(option => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleFormViewChange(option.value)}
                    aria-pressed={activeFormView === option.value}
                    className={`rounded-[16px] border px-4 py-2.5 text-left transition ${
                      activeFormView === option.value
                        ? "border-emerald-200 bg-emerald-50 shadow-sm"
                        : "border-transparent bg-slate-50/70 hover:border-slate-200 hover:bg-white"
                    }`}
                  >
                    <p
                      className={`text-sm font-semibold ${
                        activeFormView === option.value
                          ? "text-emerald-700"
                          : "text-slate-700"
                      }`}
                    >
                      {option.label}
                    </p>
                    <p className="mt-0.5 text-xs leading-5 text-slate-500">
                      {option.description}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            key={data?.id ?? "new"}
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_50px_-34px_rgba(15,23,42,0.28)] sm:p-5"
          >
            {isBulkPreviewActive ? (
              <div className="space-y-4">
                <div className="rounded-[22px] border border-sky-100 bg-[linear-gradient(135deg,#f7fcff_0%,#ffffff_55%,#eff6ff_100%)] p-4 shadow-[0_16px_35px_-30px_rgba(59,130,246,0.22)]">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-sky-700">
                        Bulk Upload
                      </p>
                      <p className="mt-1 text-sm leading-6 text-slate-500">
                        Upload one Excel sheet to add multiple fuel expense
                        entries in a single request.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_-30px_rgba(15,23,42,0.22)]">
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls"
                    onChange={e => {
                      handleBulkExcelChange(e.target.files);
                      e.currentTarget.value = "";
                    }}
                  />
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-950">
                        Upload Spreadsheet
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Accepted formats: `.xlsx`. The file will be validated on
                        the server after upload.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleTemplateAction}
                      className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                    >
                      Download Template
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => bulkFileInputRef.current?.click()}
                    className="mt-5 w-full rounded-[22px] border-2 border-dashed border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#eff6ff_100%)] px-6 py-12 text-center transition hover:border-sky-300 hover:bg-sky-50/40"
                  >
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sky-100 text-sky-700 shadow-sm">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth="1.6"
                          d="M12 16V5m0 0l-4 4m4-4l4 4M5 19h14"
                        />
                      </svg>
                    </div>
                    <p className="mt-4 text-base font-semibold text-slate-900">
                      {bulkExcelFile ? bulkExcelFile.name : "Choose Excel file"}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {bulkExcelFile
                        ? `${Math.max(1, Math.round(bulkExcelFile.size / 1024))} KB selected. Click to replace the file.`
                        : "Click here to select your expense spreadsheet from your computer."}
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {["XLSX"].map(fileType => (
                        <span
                          key={fileType}
                          className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm"
                        >
                          {fileType}
                        </span>
                      ))}
                    </div>
                  </button>

                  {bulkExcelError && (
                    <p className="mt-3 text-sm font-medium text-rose-600">
                      {bulkExcelError}
                    </p>
                  )}

                  {bulkExcelFile && (
                    <div className="mt-4 flex flex-col gap-3 rounded-[18px] border border-slate-200 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          Ready to upload
                        </p>
                        <p className="mt-1 text-sm text-slate-500">
                          {bulkExcelFile.name}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={clearBulkExcelFile}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                      >
                        Remove File
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 rounded-[22px] border border-emerald-100 bg-[linear-gradient(135deg,#f5fff9_0%,#ffffff_55%,#f8fafc_100%)] p-4 shadow-[0_14px_34px_-28px_rgba(16,185,129,0.24)]">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">
                        Calculation Method
                      </p>
                      <p className="mt-1 text-lg font-semibold text-slate-950">
                        {selectedCalculationOption.label}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        {selectedCalculationOption.label} is auto-calculated
                        from the other two values.
                      </p>
                    </div>

                    <Popover
                      placement="bottom-end"
                      offset={12}
                      showArrow
                      isOpen={isCalculationPopoverOpen}
                      onOpenChange={setIsCalculationPopoverOpen}
                    >
                      <PopoverTrigger>
                        <button
                          type="button"
                          className="flex min-w-[220px] items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-left shadow-[0_12px_30px_-26px_rgba(15,23,42,0.24)] transition hover:border-emerald-300 hover:shadow-[0_16px_36px_-28px_rgba(16,185,129,0.28)] max-sm:min-w-full"
                        >
                          <div className="flex min-w-0 flex-col">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-600">
                              Auto-fill
                            </span>
                            <span className="mt-1 truncate text-sm font-semibold text-slate-950">
                              {selectedCalculationOption.label}
                            </span>
                          </div>
                          <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Change
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[340px] rounded-[24px] border border-slate-200 bg-white p-2 shadow-2xl">
                        <div className="flex w-full flex-col gap-2 p-1">
                          <div className="px-2 pb-1">
                            <p className="text-sm font-semibold text-gray-900">
                              Calculation Settings
                            </p>
                            <p className="text-xs leading-5 text-gray-500">
                              Choose which value should be filled automatically.
                            </p>
                          </div>

                          {CALCULATION_OPTIONS.map(option => {
                            const isSelected =
                              option.value === calculationMethod;

                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() =>
                                  handleCalculationMethodChange(option.value)
                                }
                                className={`flex items-start gap-3 rounded-[20px] border p-3 text-left transition ${
                                  isSelected
                                    ? "border-emerald-500 bg-emerald-50 shadow-sm"
                                    : "border-transparent bg-gray-50/80 hover:border-gray-200 hover:bg-white"
                                }`}
                              >
                                <span
                                  className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border ${
                                    isSelected
                                      ? "border-emerald-500 bg-emerald-500"
                                      : "border-gray-300 bg-white"
                                  }`}
                                  aria-hidden="true"
                                >
                                  <span
                                    className={`h-2 w-2 rounded-full ${
                                      isSelected ? "bg-white" : "bg-transparent"
                                    }`}
                                  />
                                </span>
                                <span className="flex-1">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {option.label}
                                  </span>
                                  <span className="mt-1 block text-xs leading-5 text-gray-500">
                                    {option.description}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Row 1 */}
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {/* Vehicle */}
                    <Controller
                      name="vehicleId"
                      control={control}
                      render={({field, fieldState}) => {
                        const selectedOpt =
                          vehicles.find(
                            (v: any) => String(v.id) === String(field.value)
                          ) ?? null;
                        return (
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="vehicleId"
                              className={`${FIELD_LABEL_CLASSNAME} ${
                                fieldState.invalid
                                  ? "text-[#f31260]"
                                  : "text-slate-900"
                              }`}
                            >
                              Vehicle No{" "}
                              <span
                                className="text-[#EF4444]"
                                aria-hidden="true"
                              >
                                *
                              </span>
                            </label>
                            <Select
                              inputId="vehicleId"
                              value={selectedOpt}
                              onChange={(opt: any) => {
                                field.onChange(opt ? String(opt.id) : "");
                                clearErrors("vehicleId");
                              }}
                              options={vehicles}
                              getOptionLabel={(e: any) => e.regNo}
                              getOptionValue={(e: any) => String(e.id)}
                              placeholder="Select Vehicle"
                              styles={getReactSelectStyles(fieldState.invalid)}
                            />
                            {fieldState.error && (
                              <p
                                className="text-xs text-[#f31260]"
                                id="vehicleId-error"
                              >
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />

                    {/* Date */}
                    <Controller
                      name="date"
                      control={control}
                      render={({
                        field: {ref, value, onChange, ...fieldRest}
                      }) => (
                        <NeoDatePicker
                          {...fieldRest}
                          label={
                            <>
                              Date{" "}
                              <span
                                className="text-[#EF4444]"
                                aria-hidden="true"
                              >
                                *
                              </span>
                            </>
                          }
                          placeholder="Please select from date"
                          errorMessage={errors.date && `${errors.date.message}`}
                          maxDate={maxDate}
                          value={value}
                          className="gap-1.5"
                          labelClassName="text-[15px] font-semibold tracking-[-0.01em] text-slate-900"
                          inputClassName="!h-14 !min-h-14 !rounded-[18px] !border-[1.5px] !border-slate-200 !bg-white !px-4 !text-[15px] !text-slate-900 placeholder:!text-slate-400 hover:!border-slate-300"
                          handleDateChange={(e: any) => {
                            onChange(e);
                            onDateChange("date", e);
                          }}
                        />
                      )}
                    />
                    {/* Fuel Type */}
                    <Controller
                      name="fuelType"
                      control={control}
                      render={({field, fieldState}) => {
                        const currentId = fueltypes.find(
                          f => String(f.id) === String(field.value)
                        )?.id;
                        const selectedKeys = currentId
                          ? new Set<string>([currentId])
                          : new Set<string>();
                        return (
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="fuelType"
                              className={`${FIELD_LABEL_CLASSNAME} ${
                                fieldState.invalid
                                  ? "text-[#f31260]"
                                  : "text-slate-900"
                              }`}
                            >
                              Fuel Type{" "}
                              <span
                                className="text-[#EF4444]"
                                aria-hidden="true"
                              >
                                *
                              </span>
                            </label>
                            <NeoSelect
                              id="fuelType"
                              aria-label="Fuel Type"
                              placeholder="Please select fuel type"
                              color={fieldState.invalid ? "danger" : "default"}
                              isInvalid={!!fieldState.error}
                              errorMessage={fieldState.error?.message as string}
                              selectedKeys={selectedKeys}
                              selectionMode="single"
                              classNames={{
                                value: "text-[15px] text-slate-900",
                                selectorIcon: "text-slate-400",
                                helperWrapper: "px-1 pt-2",
                                errorMessage: "text-xs font-medium",
                                trigger: `h-14 min-h-14 rounded-[18px] border-[1.5px] bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition ${
                                  fieldState.invalid
                                    ? "border-[#f31260] data-[hover=true]:border-[#f31260] data-[focus=true]:border-[#f31260] data-[open=true]:border-[#f31260]"
                                    : "border-slate-200 data-[hover=true]:border-slate-300 data-[focus=true]:border-emerald-400 data-[open=true]:border-emerald-400"
                                }`
                              }}
                              onSelectionChange={keys => {
                                const id = Array.from(
                                  keys as Set<React.Key>
                                )[0] as string | undefined;
                                field.onChange(id ?? "");
                              }}
                            >
                              {fueltypes.map(e => (
                                <SelectItem key={e.id} value={e.id}>
                                  {e.name}
                                </SelectItem>
                              ))}
                            </NeoSelect>
                          </div>
                        );
                      }}
                    />
                  </div>

                  {/* Row 2 */}
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {/* Quantity */}
                    <Controller
                      name="qty"
                      control={control}
                      render={({field}) => (
                        <NeoTextBox
                          label={
                            <>
                              Quantity (Liters){" "}
                              <span
                                className="text-[#EF4444]"
                                aria-hidden="true"
                              >
                                *
                              </span>
                            </>
                          }
                          id="qty"
                          placeholder="0.00"
                          color={errors.qty ? "danger" : "default"}
                          isInvalid={!!errors.qty}
                          errorMessage={errors.qty?.message as string}
                          description={getAutoCalculatedDescription("qty")}
                          isReadOnly={isAutoCalculatedField("qty")}
                          classNames={getFuelFieldClassNames(
                            isAutoCalculatedField("qty")
                          )}
                          endContent={
                            isAutoCalculatedField("qty") ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                Auto
                              </span>
                            ) : undefined
                          }
                          onChange={(e: any) => {
                            if (isAutoCalculatedField("qty")) return;
                            setValue("qty", e.target.value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                            recalculateAutoField();
                          }}
                          value={
                            field.value !== undefined && field.value !== null
                              ? String(field.value)
                              : ""
                          }
                        />
                      )}
                    />
                    {/* Fuel Rate */}
                    <Controller
                      name="fuelRate"
                      control={control}
                      render={({field}) => (
                        <NeoTextBox
                          label={
                            <>
                              Rate per liter{" "}
                              <span
                                className="text-[#EF4444]"
                                aria-hidden="true"
                              >
                                *
                              </span>
                            </>
                          }
                          id="fuelRate"
                          placeholder="0.00"
                          color={errors.fuelRate ? "danger" : "default"}
                          isInvalid={!!errors.fuelRate}
                          errorMessage={errors.fuelRate?.message as string}
                          description={getAutoCalculatedDescription("fuelRate")}
                          isReadOnly={isAutoCalculatedField("fuelRate")}
                          classNames={getFuelFieldClassNames(
                            isAutoCalculatedField("fuelRate")
                          )}
                          endContent={
                            isAutoCalculatedField("fuelRate") ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                Auto
                              </span>
                            ) : undefined
                          }
                          onChange={(e: any) => {
                            if (isAutoCalculatedField("fuelRate")) return;
                            setValue("fuelRate", e.target.value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                            recalculateAutoField();
                          }}
                          value={
                            field.value !== undefined && field.value !== null
                              ? String(field.value)
                              : ""
                          }
                        />
                      )}
                    />

                    {/* Total Amount */}
                    <Controller
                      name="totalAmount"
                      control={control}
                      render={({field}) => (
                        <NeoTextBox
                          label={
                            <>
                              Total Cost{" "}
                              <span
                                className="text-[#EF4444]"
                                aria-hidden="true"
                              >
                                *
                              </span>
                            </>
                          }
                          id="totalAmount"
                          placeholder="0.00"
                          color={errors.totalAmount ? "danger" : "default"}
                          isInvalid={!!errors.totalAmount}
                          errorMessage={errors.totalAmount?.message as string}
                          description={getAutoCalculatedDescription(
                            "totalAmount"
                          )}
                          isReadOnly={isAutoCalculatedField("totalAmount")}
                          classNames={getFuelFieldClassNames(
                            isAutoCalculatedField("totalAmount")
                          )}
                          endContent={
                            isAutoCalculatedField("totalAmount") ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                Auto
                              </span>
                            ) : undefined
                          }
                          onChange={(e: any) => {
                            if (isAutoCalculatedField("totalAmount")) return;
                            setValue("totalAmount", e.target.value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                            recalculateAutoField();
                          }}
                          value={
                            field.value !== undefined && field.value !== null
                              ? String(field.value)
                              : ""
                          }
                        />
                      )}
                    />
                  </div>

                  {/* Row 3 */}
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {/* Fuel Station */}
                    <Controller
                      name="fuelStationId"
                      control={control}
                      render={({field}) => {
                        const valueId =
                          field.value == null || field.value === ""
                            ? null
                            : String(field.value);
                        const selectedOption = valueId
                          ? (fuelstations.find(
                              (o: any) => String(o.id) === valueId
                            ) ?? null)
                          : null;
                        return (
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="fuelStationId"
                              className="flex items-center justify-between gap-3"
                            >
                              <span className={FIELD_LABEL_CLASSNAME}>
                                Fuel Station
                              </span>
                              <button
                                type="button"
                                title="Add new fuel station"
                                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-100"
                                onClick={() => openPopup()}
                              >
                                <PlusIcon size={15} />
                                Add Station
                              </button>
                            </label>
                            <Select
                              inputId="fuelStationId"
                              name={field.name}
                              value={selectedOption}
                              options={fuelstations}
                              onChange={(opt: any) =>
                                field.onChange(opt ? String(opt.id) : null)
                              }
                              getOptionLabel={(e: any) => e.name}
                              getOptionValue={(e: any) => String(e.id)}
                              placeholder="Select fuel station"
                              styles={getReactSelectStyles(false)}
                            />
                          </div>
                        );
                      }}
                    />

                    {/* Odometer */}
                    <Controller
                      name="odometerReading"
                      control={control}
                      render={({field}) => (
                        <NeoTextBox
                          label="Odometer Reading"
                          id="odometerReading"
                          placeholder="12345"
                          color={errors.odometerReading ? "danger" : "default"}
                          isInvalid={!!errors.odometerReading}
                          errorMessage={
                            errors.odometerReading?.message as string
                          }
                          classNames={getFuelFieldClassNames()}
                          {...field}
                          value={
                            field.value !== undefined && field.value !== null
                              ? String(field.value)
                              : ""
                          }
                        />
                      )}
                    />

                    {/* Note */}
                    <Controller
                      name="description"
                      control={control}
                      render={({field}) => (
                        <NeoTextBox
                          label="Note"
                          id="description"
                          placeholder="Add note"
                          classNames={getFuelFieldClassNames()}
                          {...field}
                          value={field.value ?? ""}
                        />
                      )}
                    />
                  </div>

                  {isCreateMode && (
                    <UploadFormField
                      value={attachments}
                      onChangeFile={setAttachments}
                      max={3}
                      variant="fuel"
                      className="grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(260px,0.9fr)]"
                      onError={msg => console.error(msg)}
                    />
                  )}
                </div>
              </>
            )}

            <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-end">
              {isBulkPreviewActive ? (
                <>
                  <NeoButton
                    type="button"
                    color="white"
                    onClick={() => handleFormViewChange("single")}
                    className="h-12 rounded-[16px] border-slate-200 px-7 text-sm font-semibold"
                  >
                    Back to Form
                  </NeoButton>
                  <NeoButton
                    type="button"
                    color="black"
                    onClick={handleBulkUploadSubmit}
                    disabled={!bulkExcelFile || isBulkUploading}
                    isLoading={isBulkUploading}
                    spinner={
                      <AiOutlineLoading
                        size="xs"
                        className="animate-spin h-6 w-6"
                      />
                    }
                    className="h-12 rounded-[16px] px-7 text-sm font-semibold"
                  >
                    Upload File
                  </NeoButton>
                </>
              ) : (
                <>
                  <NeoButton
                    type="button"
                    color="white"
                    onClick={onCancel}
                    className="h-12 rounded-[16px] border-slate-200 px-7 text-sm font-semibold"
                  >
                    Reset
                  </NeoButton>
                  <NeoButton
                    type="submit"
                    disabled={isSubmitting || loading}
                    isLoading={isSubmitting || loading}
                    spinner={
                      <AiOutlineLoading
                        size="xs"
                        className="animate-spin h-6 w-6"
                      />
                    }
                    color="black"
                    className="h-12 rounded-[16px] px-7 text-sm font-semibold"
                  >
                    Save
                  </NeoButton>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
      <NeoModal
        onOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        actionType={modalName}
        hasActions={false}
        data={modalData}
        onModalSubmit={handleFormSubmit}
        actionText={"Fuel Station"}
      />
    </>
  );
};
